import { useEffect, useRef } from 'react'
import { createFileRoute, Outlet, Link, useNavigate, useLocation, type ErrorComponentProps } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { apiV2 } from '@/lib/api-v2-client'
import { DashboardSidebar } from '@/components/dashboard-sidebar'
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar'
import { Separator } from '@/components/ui/separator'
import { hasProductAccess } from '@/lib/subscription-access'
import { isSafeRedirect, takeRememberedRedirect } from '@/lib/post-auth-redirect'
import { flushAttribution } from '@/lib/attribution'
import { hardNavigate, onSessionExpired, sessionExpiredLoginUrl } from '@/lib/session-expiry'
import { PlanPicker } from '@/components/plan-picker'
import { PastDueBanner } from '@/components/past-due-banner'
import { CreateMonitorBlockBanner } from '@/components/create-monitor-block'
import { LogOut } from 'lucide-react'

export const Route = createFileRoute('/_authenticated')({
  component: AuthenticatedLayout,
  errorComponent: AuthErrorComponent,
})

export function AuthenticatedLayout() {
  const { isLoading, isAuthenticated, impersonatingAdmin, user, organization, refreshAuth } = useAuth()
  const navigate = useNavigate()
  const { pathname, href: currentHref } = useLocation()
  const productAccess = hasProductAccess(organization)
  const accountPage = pathname === '/dashboard/settings' || pathname === '/dashboard/settings/'
  // /checkout renders its own plan picker and must stay reachable without a plan.
  const checkoutPage = pathname === '/checkout' || pathname === '/checkout/'
  const adminPage = user?.is_admin && (pathname === '/admin' || pathname.startsWith('/admin/'))
  const queryClient = useQueryClient()
  const redirectChecked = useRef(false)

  const stopImpersonation = useMutation({
    mutationFn: () => apiV2.stopImpersonation(),
    onSuccess: async () => {
      await refreshAuth()
      queryClient.clear()
      toast.success('Returned to your admin account')
      navigate({ to: '/admin' })
    },
    onError: () => toast.error('Failed to stop impersonation'),
  })

  // After auth (e.g. OAuth callback), check for a stored redirect destination
  useEffect(() => {
    if (!isLoading && isAuthenticated && !redirectChecked.current) {
      redirectChecked.current = true
      void flushAttribution(apiV2.postAttribution)
      const savedRedirect = takeRememberedRedirect()
      if (savedRedirect) {
        navigate({ href: savedRedirect })
      }
    }
  }, [isLoading, isAuthenticated, navigate])

  const mustLogin = !isLoading && !isAuthenticated
  // Navigate in an effect, not during render (render-time navigate loops).
  // Keep where the user was going (e.g. /checkout?plan=pro) across login.
  // Runs once: while the router moves to /login this layout can re-render
  // with the new URL, which must not be nested into another redirect.
  const loginRedirected = useRef(false)
  useEffect(() => {
    if (!mustLogin || loginRedirected.current) return
    loginRedirected.current = true
    const back = currentHref && isSafeRedirect(currentHref) && !currentHref.startsWith('/login') ? currentHref : undefined
    navigate({ to: '/login', search: { redirect: back } })
  }, [mustLogin, currentHref, navigate])

  // A 401 from the API while the user is signed in means the session ended. Send the
  // user to sign in and bring them back to this page. The handler runs once per page load.
  useEffect(() => {
    if (!isAuthenticated) return
    return onSessionExpired(() => hardNavigate(sessionExpiredLoginUrl(window.location)))
  }, [isAuthenticated])

  if (mustLogin) {
    return null
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <SidebarProvider>
      {productAccess && <DashboardSidebar />}
      <SidebarInset>
        {impersonatingAdmin && (
          <div className="flex items-center justify-between gap-3 border-b border-status-degraded bg-status-degraded-soft px-4 py-2 text-sm">
            <div className="flex min-w-0 flex-1 items-center gap-2 text-status-degraded">
              <span className="shrink-0 font-semibold">Impersonating</span>
              <span className="truncate">
                {user?.name} ({user?.email})
              </span>
              <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
                · admin: {impersonatingAdmin.email}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 border-status-degraded bg-transparent text-status-degraded hover:bg-status-degraded-soft"
              disabled={stopImpersonation.isPending}
              onClick={() => stopImpersonation.mutate()}
            >
              <LogOut className="mr-1 h-3.5 w-3.5" />
              Exit impersonation
            </Button>
          </div>
        )}
        <header className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
          {/* The toggle and its line need the sidebar. Without product access no sidebar is mounted. */}
          {productAccess && (
            <>
              <SidebarTrigger className="-ml-1" />
              <Separator orientation="vertical" className="mr-2 !h-4" />
            </>
          )}
          <span className="text-sm text-muted-foreground">Uptrack</span>
        </header>
        <PastDueBanner />
        {/* On /checkout the user is already taking the Pro way out. */}
        {!checkoutPage && <CreateMonitorBlockBanner />}
        <div id="main-content" tabIndex={-1} className="min-w-0 flex-1 px-4 py-6 outline-none md:px-6 lg:px-8">
          {productAccess || accountPage || checkoutPage || adminPage ? <Outlet /> : (
            <div className="mx-auto flex max-w-4xl flex-col items-center gap-4 py-10 text-center">
              <h1 className="text-3xl font-semibold">Choose a plan to start monitoring</h1>
              <p className="text-muted-foreground">Monitoring and alerts require Pro, Team, or Business. Your saved monitors and settings are kept for when you subscribe.</p>
              <PlanPicker />
              <Link to="/dashboard/settings" search={{ billing: undefined, plan: undefined, tab: 'billing' }} className="text-sm underline">Account and billing settings</Link>
              {user?.is_admin && <Link to="/admin" className="text-sm underline">Open admin</Link>}
            </div>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}

function AuthErrorComponent({ error, reset }: ErrorComponentProps) {
  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 text-center">
      <h2 className="text-2xl font-semibold">Something went wrong</h2>
      <p className="max-w-md text-muted-foreground">
        {error instanceof Error ? error.message : 'An unexpected error occurred.'}
      </p>
      <div className="flex gap-2">
        <Button onClick={reset}>Try Again</Button>
        <Button variant="outline" asChild>
          <Link to="/dashboard">Go to Dashboard</Link>
        </Button>
      </div>
    </div>
  )
}
